/**
 * Database Operations Page
 * Phase 1-6: Complete implementation with all overview, tables, and performance components
 *
 * Features:
 * - Overview cards (Health, Size, Query Performance, Connection Pool)
 * - Data freshness timeline
 * - Storage breakdown chart (Recharts pie chart)
 * - Tables tab with full statistics, sorting, filtering, expandable rows
 * - Slow queries tab
 * - Query performance by type
 * - Connection pool monitoring
 * - Phase 6: Lazy-loaded tabs for optimal bundle size
 */

import { useState, useEffect } from 'react';
import { Database, Activity, HardDrive, Gauge, BarChart3, Table2, Zap, Trash2, Clock } from 'lucide-react';
import { useDbStats, useDataFreshness } from '../../../hooks/admin';
import { OverviewCards, OverviewCardData } from '../OverviewCards';
import { FreshnessTimeline } from '../FreshnessTimeline';
import { StorageBreakdownChart } from '../StorageBreakdownChart';
import { IndexUsageSection } from './IndexUsageSection';
import { ConnectionPoolSection } from './ConnectionPoolSection';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

// Phase 6: Lazy-loaded tabs for code splitting and performance
import {
  TablesTab,
  PerformanceTab,
  CleanupTab,
  JobsTab,
  preloadOnIdle,
  LazyTablesTab,
  LazyPerformanceTab,
  LazyCleanupTab,
  LazyJobsTab,
} from './LazyComponents';

// Eagerly loaded components (needed for overview)
import { SlowQueriesTab } from './SlowQueriesTab';

type TabId = 'overview' | 'tables' | 'performance' | 'slow-queries' | 'connection-pool' | 'cleanup' | 'jobs';

interface Tab {
  id: TabId;
  label: string;
  icon: React.ReactNode;
}

const TABS: Tab[] = [
  { id: 'overview', label: 'Overview', icon: <Activity className="h-4 w-4" /> },
  { id: 'tables', label: 'Tables', icon: <Table2 className="h-4 w-4" /> },
  { id: 'performance', label: 'Performance', icon: <Zap className="h-4 w-4" /> },
  { id: 'slow-queries', label: 'Slow Queries', icon: <BarChart3 className="h-4 w-4" /> },
  { id: 'connection-pool', label: 'Connection Pool', icon: <Gauge className="h-4 w-4" /> },
  { id: 'cleanup', label: 'Cleanup', icon: <Trash2 className="h-4 w-4" /> },
  { id: 'jobs', label: 'Jobs', icon: <Clock className="h-4 w-4" /> },
];

/**
 * Loading skeleton for the entire page
 */
function PageSkeleton() {
  return (
    <div className="space-y-6">
      {/* Overview cards skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <Skeleton className="h-4 w-24 mb-4" />
              <Skeleton className="h-10 w-20 mb-2" />
              <Skeleton className="h-4 w-32" />
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Freshness timeline skeleton */}
      <Card>
        <CardContent className="p-6">
          <Skeleton className="h-6 w-32 mb-4" />
          <div className="space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1">
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-3 w-48" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Error state component
 */
function ErrorState({ message }: { message: string }) {
  return (
    <Card className="border-red-200 dark:border-red-800">
      <CardContent className="p-6">
        <div className="flex items-center gap-3 text-red-600 dark:text-red-400">
          <Database className="h-6 w-6" />
          <div>
            <p className="font-semibold">Error loading database statistics</p>
            <p className="text-sm text-red-500 dark:text-red-400">{message}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function DatabasePage() {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const { stats, isLoading: statsLoading, isError: statsError } = useDbStats();
  const { isLoading: freshnessLoading } = useDataFreshness();

  // Phase 6: Preload lazy components on idle for instant tab switching
  useEffect(() => {
    // Preload all tabs after 2 seconds of idle time
    preloadOnIdle(LazyTablesTab, 2000);
    preloadOnIdle(LazyPerformanceTab, 2500);
    preloadOnIdle(LazyCleanupTab, 3000);
    preloadOnIdle(LazyJobsTab, 3500);
  }, []);

  // Build overview cards data from stats
  const overviewCards: OverviewCardData[] = stats ? [
    {
      title: 'Database Health',
      value: stats.health_score,
      icon: Activity,
      variant: stats.health_score >= 90 ? 'success' : stats.health_score >= 70 ? 'warning' : 'danger',
      description: `Status: ${stats.connection_status}`,
      trend: undefined, // Could add trend from historical data
    },
    {
      title: 'Database Size',
      value: stats.total_db_size,
      icon: HardDrive,
      variant: 'default',
      description: `${stats.tables.length} tables`,
    },
    {
      title: 'Query Performance',
      value: stats.queries.avg_time_ms !== null
        ? `${stats.queries.avg_time_ms.toFixed(1)}ms`
        : 'N/A',
      icon: Zap,
      variant: stats.queries.slow_queries_1h !== null && stats.queries.slow_queries_1h > 5
        ? 'warning'
        : 'default',
      description: stats.queries.slow_queries_1h !== null
        ? `${stats.queries.slow_queries_1h} slow queries (1h)`
        : 'pg_stat_statements not enabled',
    },
    {
      title: 'Connection Pool',
      value: `${stats.connection_pool.active}/${stats.connection_pool.max}`,
      icon: Gauge,
      variant: stats.connection_pool.utilization_pct > 80
        ? 'danger'
        : stats.connection_pool.utilization_pct > 60
          ? 'warning'
          : 'success',
      description: `${stats.connection_pool.utilization_pct.toFixed(1)}% utilization`,
    },
  ] : [];

  // Loading state
  if (statsLoading || freshnessLoading) {
    return (
      <div className="admin-page">
        <div className="admin-page-header">
          <div className="flex items-center gap-3">
            <Database className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold">Database Operations</h1>
              <p className="text-muted-foreground">
                Monitor database health, performance, and storage utilization
              </p>
            </div>
          </div>
        </div>
        <div className="admin-page-content">
          <PageSkeleton />
        </div>
      </div>
    );
  }

  // Error state
  if (statsError) {
    return (
      <div className="admin-page">
        <div className="admin-page-header">
          <div className="flex items-center gap-3">
            <Database className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold">Database Operations</h1>
              <p className="text-muted-foreground">
                Monitor database health, performance, and storage utilization
              </p>
            </div>
          </div>
        </div>
        <div className="admin-page-content">
          <ErrorState message={statsError.message || 'Unknown error'} />
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      {/* Page Header */}
      <div className="admin-page-header mb-6">
        <div className="flex items-center gap-3">
          <Database className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold">Database Operations</h1>
            <p className="text-muted-foreground">
              Monitor database health, performance, and storage utilization
            </p>
          </div>
        </div>
        {stats && (
          <p className="text-sm text-muted-foreground mt-2">
            Last updated: {new Date(stats.timestamp).toLocaleString()}
          </p>
        )}
      </div>

      <div className="admin-page-content">
        {/* Tab Navigation */}
        <div className="mb-6 border-b border-border">
          <nav className="flex gap-1 -mb-px" role="tablist" aria-label="Database page tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                role="tab"
                aria-selected={activeTab === tab.id}
                aria-controls={`${tab.id}-panel`}
                className={`
                  flex items-center gap-2 px-4 py-3 text-sm font-medium
                  border-b-2 transition-colors
                  ${activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }
                `}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Panels */}

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div
            id="overview-panel"
            role="tabpanel"
            aria-labelledby="overview-tab"
            className="space-y-6"
          >
            {/* Overview Cards */}
            <OverviewCards
              cards={overviewCards}
              isLoading={false}
              columns={{ default: 1, md: 2, lg: 4 }}
            />

            {/* Data Freshness Timeline */}
            <FreshnessTimeline />

            {/* Storage Breakdown Chart */}
            <StorageBreakdownChart
              limit={10}
              showLegend={true}
            />

            {/* Index Usage Overview */}
            <IndexUsageSection />
          </div>
        )}

        {/* Tables Tab */}
        {activeTab === 'tables' && (
          <div
            id="tables-panel"
            role="tabpanel"
            aria-labelledby="tables-tab"
            className="space-y-6"
          >
            <TablesTab
              tables={stats?.tables || []}
              isLoading={statsLoading}
            />
          </div>
        )}

        {/* Performance Tab */}
        {activeTab === 'performance' && (
          <div
            id="performance-panel"
            role="tabpanel"
            aria-labelledby="performance-tab"
            className="space-y-6"
          >
            <PerformanceTab />
          </div>
        )}

        {/* Slow Queries Tab */}
        {activeTab === 'slow-queries' && (
          <div
            id="slow-queries-panel"
            role="tabpanel"
            aria-labelledby="slow-queries-tab"
          >
            <SlowQueriesTab />
          </div>
        )}

        {/* Connection Pool Tab */}
        {activeTab === 'connection-pool' && (
          <div
            id="connection-pool-panel"
            role="tabpanel"
            aria-labelledby="connection-pool-tab"
          >
            <ConnectionPoolSection />
          </div>
        )}

        {/* Cleanup Tab */}
        {activeTab === 'cleanup' && (
          <div
            id="cleanup-panel"
            role="tabpanel"
            aria-labelledby="cleanup-tab"
          >
            <CleanupTab />
          </div>
        )}

        {/* Jobs Tab */}
        {activeTab === 'jobs' && (
          <div
            id="jobs-panel"
            role="tabpanel"
            aria-labelledby="jobs-tab"
          >
            <JobsTab />
          </div>
        )}
      </div>
    </div>
  );
}
